import React, { createContext, useContext, useState, ReactNode } from 'react';
import { createLightTheme, BrandVariants, Theme, webLightTheme, webDarkTheme } from '@fluentui/react-components';

export type ThemeName = 'light' | 'dark' | 'red' | 'blue';

interface ThemeContextType {
  theme: Theme;
  themeName: ThemeName;
  setTheme: (themeName: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Brand color variants for different themes
const redBrandRamp: BrandVariants = {
  10: '#1A0000',
  20: '#330000',
  30: '#4D0000',
  40: '#660000',
  50: '#800000',
  60: '#990000',
  70: '#B30000',
  80: '#CC0000',
  90: '#E60000',
  100: '#FF0000',
  110: '#FF1A1A',
  120: '#FF3333',
  130: '#FF4D4D',
  140: '#FF6666',
  150: '#FF8080',
  160: '#FF9999',
};

const blueBrandRamp: BrandVariants = {
  10: '#000033',
  20: '#000066',
  30: '#000099',
  40: '#0000CC',
  50: '#0000FF',
  60: '#1A1AFF',
  70: '#3333FF',
  80: '#4D4DFF',
  90: '#6666FF',
  100: '#8080FF',
  110: '#9999FF',
  120: '#B3B3FF',
  130: '#CCCCFF',
  140: '#E6E6FF',
  150: '#F0F0FF',
  160: '#F5F5FF',
};

// Helper function to create custom themed backgrounds
const createCustomTheme = (baseTheme: Theme, backgroundColors: {
  bg1: string;
  bg2: string;
  bg3: string;
  bg4: string;
  fg1: string;
  fg2: string;
  fg3: string;
  stroke1: string;
  stroke2: string;
}): Theme => {
  return {
    ...baseTheme,
    colorNeutralBackground1: backgroundColors.bg1,
    colorNeutralBackground2: backgroundColors.bg2,
    colorNeutralBackground3: backgroundColors.bg3,
    colorNeutralBackground4: backgroundColors.bg4,
    colorNeutralForeground1: backgroundColors.fg1,
    colorNeutralForeground2: backgroundColors.fg2,
    colorNeutralForeground3: backgroundColors.fg3,
    colorNeutralStroke1: backgroundColors.stroke1,
    colorNeutralStroke2: backgroundColors.stroke2,
  } as Theme;
};

// Create base themes
const baseRedTheme = createLightTheme(redBrandRamp);
const baseBlueTheme = createLightTheme(blueBrandRamp);

// Create custom red theme with vibrant red-tinted backgrounds
const redTheme = createCustomTheme(baseRedTheme, {
  bg1: '#FFD6D6', // Vibrant light red - main background
  bg2: '#FFB3B3', // Medium red - cards/sections
  bg3: '#FF9999', // Medium-dark red - hover states
  bg4: '#FF8080', // Darker red - active states
  fg1: '#1A0000', // Very dark red for primary text
  fg2: '#330000', // Dark red for secondary text
  fg3: '#4D0000', // Medium dark red for tertiary text
  stroke1: '#FF6666', // Red borders
  stroke2: '#FF9999', // Light red borders
});

// Create custom blue theme with vibrant blue-tinted backgrounds
const blueTheme = createCustomTheme(baseBlueTheme, {
  bg1: '#D6E6FF', // Vibrant light blue - main background
  bg2: '#B3D1FF', // Medium blue - cards/sections
  bg3: '#99C2FF', // Medium-dark blue - hover states
  bg4: '#80B3FF', // Darker blue - active states
  fg1: '#000033', // Very dark blue for primary text
  fg2: '#000066', // Dark blue for secondary text
  fg3: '#000099', // Medium dark blue for tertiary text
  stroke1: '#6699FF', // Blue borders
  stroke2: '#99B3FF', // Light blue borders
});

// Create themes
const lightTheme = webLightTheme;
const darkTheme = webDarkTheme;

const themes: Record<ThemeName, Theme> = {
  light: lightTheme,
  dark: darkTheme,
  red: redTheme,
  blue: blueTheme,
};

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [themeName, setThemeNameState] = useState<ThemeName>(() => {
    // Load from localStorage or default to light
    const saved = localStorage.getItem('app-theme') as ThemeName;
    return saved && ['light', 'dark', 'red', 'blue'].includes(saved) ? saved : 'light';
  });

  const setTheme = (newTheme: ThemeName) => {
    setThemeNameState(newTheme);
    localStorage.setItem('app-theme', newTheme);
  };

  const theme = themes[themeName];

  return (
    <ThemeContext.Provider value={{ theme, themeName, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

